/* eslint-disable unicorn/prefer-blob-reading-methods */
import _ from 'lodash-es';
import $ from 'jquery';
import Action from '~/scripts/services/model/action';
import Defaults from '~/scripts/constants/defaults';
import Designer from '~/scripts/services/model/designer';
import Droit from '~/scripts/services/model/droit';
import LinkStyle from '~/scripts/services/model/link-style';
import Repre from '~/scripts/services/model/repre';
import State from '~/scripts/services/model/state';
import Workflow from '~/scripts/services/model/workflow';
import { ConfirmDeleteModal } from '~/utils/modals';
import APIV5 from '~/models/api';
import templateModalDroit from '~/components/droit/modal/modal-droit.html';
import ModalDroitController from '~/components/droit/modal/modal-droit.controller';
import { Confirm } from '~/scripts/services/modal/modal';
import Lang from '~/scripts/services/model/lang';

const GRID = 25;

const workflowSourceParameters = {
	filter:           '.node-connect',
	anchor:           'Continuous',
	connector:        [
		new LinkStyle().type,
	],
	maxConnections:   1000,
	onMaxConnections: (/** @type {any} */ info) => {
		// eslint-disable-next-line no-alert
		window.alert(`Maximum connections (${info.maxConnections}) reached`);
	},
};

const workflowTargetParameters = {
	allowLoopback: false,
	anchor:        'Continuous',
	dropOptions:   {
		hoverClass: 'dragHover',
	},
	isTarget:      true,
};

export default class AdminWfDesignerService extends Designer {
	/* @ngInject */
	/**
	 * Instantiates a workflow
	 *
	 * @param {import('angular').auto.IInjectorService} $injector
	 */
	constructor($injector) {
		super($injector);

		/**
		 * @type {?{
		 *   create: boolean,
		 *   modified: boolean,
		 *   node: Action | State,
		 *   operations: any,
		 *   tab: 'info' | 'droit' | 'allocator' | 'delegation'
		 * }}
		 */
		this.selected = null;

		/** @type {Workflow} */
		this.schema = new Workflow($injector);

		/** @type {boolean} */
		this.loaded = false;
	}

	/**
	 * Deletes an action
	 *
	 * @param {Action} action Instance of action
	 */
	actionDelete(action) {
		let actionId = -1;

		for (const [k, a] of this.schema.actions.entries()) {
			if (a.id === action.id) {
				actionId = k;
			}
		}

		if (actionId >= 0 && this.jsp) {
			for (const [state, connection] of Object.entries(action.statefrom)) {
				// @ts-ignore
				this.jsp?.deleteConnection(connection, { force: true });
				delete action.statefrom[state];
			}
			for (const [state, connection] of Object.entries(action.stateto)) {
				// @ts-ignore
				this.jsp?.deleteConnection(connection, { force: true });
				delete action.stateto[state];
			}
			this.schema.actions.splice(actionId, 1);
			this.modified = true;
		}
	}

	/**
	 * Updates the connections of an action according to params
	 *
	 * @param {Action} action Instance of Action
	 * @param {any} operations List of operations
	 */
	actionUpdateConnections(action, operations) {
		for (const [kid, op] of Object.entries(operations)) {
			// @ts-ignore
			const state = this.schema.states[this.schema.getKeyStateFromKid(kid)];
			if (op.op === 'ADD') {
				if (op.flow === 'from') {
					const connection = this.jsp?.connect({
						source:    `kn-state-${this.id}-${state.id}`,
						target:    `kn-action-${this.id}-${action.id}`,
						connector: action.style.type,
						cssClass:  action.style.getCSS(),
						// @ts-ignore
						anchors:   ['Continuous'],
					});
					if (connection && state.kid) {
						// @ts-ignore
						connection.getOverlay('label').setLabel('FROM');
						action.statefrom[state.kid] = connection;
					}
				}
				else if (op.flow === 'to') {
					const connection = this.jsp?.connect({
						source:    `kn-action-${this.id}-${action.id}`,
						target:    `kn-state-${this.id}-${state.id}`,
						connector: action.style.type,
						cssClass:  action.style.getCSS(),
						// @ts-ignore
						anchors:   ['Continuous'],
					});
					if (connection && state.kid) {
						// @ts-ignore
						connection.getOverlay('label').setLabel('TO');
						action.stateto[state.kid] = connection;
					}
				}
			}
			else if (op.op === 'DEL') {
				if (op.flow === 'from') {
					// @ts-ignore
					this.jsp?.deleteConnection(action.statefrom[kid], { force: true });
					delete action.statefrom[kid];
				}
				else if (op.flow === 'to') {
					// @ts-ignore
					this.jsp?.deleteConnection(action.stateto[kid], { force: true });
					delete action.stateto[kid];
				}
			}
		}
	}

	/**
	 * Create a new Action node between two State nodes
	 *
	 * @param {State} source Source state
	 * @param {State} target Target state
	 */
	async createActionBetweenStates(source, target) {
		const newAction = new Action(this.$injector);
		if (source.kid && target.kid) {
			newAction.statefrom[source.kid] = undefined;
			newAction.stateto[target.kid] = undefined;

			const repre = new Repre();
			newAction.addRepre(repre);

			const newEvent = new Event('createActionBetweenStates');
			// @ts-ignore
			newEvent.offsetX = (source.x + target.x) / 2;
			// @ts-ignore
			newEvent.offsetY = (source.y + target.y) / 2;

			const operations = {
				[source.kid]: {
					op:   'ADD',
					flow: 'from',
				},
				[target.kid]: {
					op:   'ADD',
					flow: 'to',
				},
			};

			// @ts-ignore
			const action = await this.nodeCreateFromEvent(newEvent, newAction);
			await this.nodeEdit(action, 'info', true, operations);
		}
	}

	/**
	 * Create new schema and apply default rights
	 *
	 * @param {import('@knitiv/api-client-javascript').KNode} kid - K identifier of the new schema
	 */
	async createSchema(kid) {
		this.schema.kid = `K_NODE;${this.User.getRef()}`;
		this.create = true;
		this.edit = true;
		this.id = kid.split(';')[1];

		const newDroit = new Droit({
			kright: Defaults.DROIT.READ,
			role: {
				kid: 'K_NODE;KR_WF_TOUS',
				isa: 'K_NODE;KROLE',
				name: this.$translate.instant('common.all'),
			},
		});
		await this.schema.addDroit(newDroit);
	}

	/**
	 * Displays the content of the tab
	 */
	display() {
		const $wfContainer = $('.tab-pane:visible').find('.schema-content');
		const $wfEditor = $wfContainer.find('.schema-editor');

		// ---- d&d schema ----
		/** @type {boolean} */
		let clicking = false;

		/** @type {boolean} */
		let moving = false;

		/** @type {number } */
		let previousX;

		/** @type {number} */
		let previousY;

		if (this.jsp) {
			// @ts-ignore
			this.jsp.Defaults.Container = $wfEditor;
		}
		this.schemaOffset = $wfEditor.offset();

		if (this.schema) {
			this.schema.area.width = $wfContainer.width();
			this.schema.area.height = $wfContainer.height();
			this.schema.autoplaceNodes();
		}

		$wfEditor.bind('mousedown', event => {
			// @ts-ignore
			if ($(event.target).hasClass('schema-editor')) {
				event.preventDefault();
				previousX = event.clientX;
				previousY = event.clientY;
				clicking = true;
			}
		});

		$(document).bind('mouseup', event => {
			// Deselect previously selected node
			// @ts-ignore
			if (!moving && $(event.target).hasClass('schema-editor')) {
				this.$timeout(async () => {
					if (this.selected) {
						if (this.selected.modified) {
							await this.nodeConfirmChanges();
						}
						else {
							this.selected.node.selected = false;
							this.selected = null;
						}
					}
				});
			}
			clicking = false;
			moving = false;
		});

		$wfEditor.bind('mousemove', event => {
			if ($wfContainer && clicking) {
				moving = true;
				event.preventDefault();
				$wfContainer.scrollLeft(($wfContainer.scrollLeft() || 0) + (previousX - event.clientX));
				$wfContainer.scrollTop(($wfContainer.scrollTop() || 0) + (previousY - event.clientY));
				previousX = event.clientX;
				previousY = event.clientY;
			}
		});

		$wfEditor.bind('mouseleave', () => {
			clicking = false;
		});

		// ---- zoom schema ----
		$(window).bind('keydown', event => {
			if (event.keyCode === 107 && (event.ctrlKey || event.metaKey)) {
				event.preventDefault();
				this.zoomIn();
			}
			else if (event.keyCode === 109 && (event.ctrlKey || event.metaKey)) {
				event.preventDefault();
				this.zoomOut();
			}
		});
		$wfEditor.bind('mousewheel DOMMouseScroll', event => {
			// @ts-ignore
			if ((event.ctrlKey || event.metaKey) && event.originalEvent.deltaY < 0) {
				event.preventDefault();
				this.zoomIn();
			}
			// @ts-ignore
			else if ((event.ctrlKey || event.metaKey) && event.originalEvent.deltaY > 0) {
				event.preventDefault();
				this.zoomOut();
			}
		});

		// eslint-disable-next-line unicorn/prefer-prototype-methods
		this.jsp?.bind('beforeDrop', info => {
			const source = $(`#${info.sourceId}`).scope().$parent;
			const destination = $(`#${info.targetId}`).scope().$parent;

			if ('state' in source && 'state' in destination) {
				// @ts-ignore
				this.createActionBetweenStates(source.state, destination.state);
			}

			return ('state' in source && 'action' in destination) || ('action' in source && 'state' in destination);
		});

		// event sur connection entre 2 états (ajout/modif d'une action)
		// eslint-disable-next-line unicorn/prefer-prototype-methods
		this.jsp?.bind('connection', info => {
			const source = $(`#${info.sourceId}`).scope().$parent;
			const destination = $(`#${info.targetId}`).scope().$parent;

			let action;

			if ('state' in source && 'action' in destination) {
				// @ts-ignore
				({ action } = destination);
				// @ts-ignore
				action.statefrom[source.state.kid] = info.connection;
			}
			else if ('action' in source && 'state' in destination) {
				// @ts-ignore
				({ action } = source);
				// @ts-ignore
				action.stateto[destination.state.kid] = info.connection;
			}

			if (action) {
				// @ts-ignore
				info.connection.canvas.classList = [];
				// @ts-ignore
				info.connection.addClass(action.style.getCSS());
			}
		});

		// eslint-disable-next-line unicorn/prefer-prototype-methods
		this.jsp?.bind('beforeDetach', () => false);

		// $wfEditor.bind('click', event => {
		// });

		this.$timeout(() => {
			if (!this.loaded) {
				// suspend drawing and initialise.
				const $nodeList = $wfEditor.find('.node');

				// mettre à jour la position x/y en cas de déplacement
				this.jsp?.draggable($nodeList, {
					// @ts-ignore
					containment:   $wfEditor,
					stop:          (/** @type {any} */ element) => this.nodeMoveStop(element),
					disabled:      !this.edit,
					snapThreshold: 13,
					grid:          [
						GRID,
						GRID,
					],
				});
				this.jsp?.setDraggable($nodeList, this.edit);

				if ($nodeList.length > 0) {
					this.jsp?.batch(() => {
						// @ts-ignore
						this.jsp?.makeSource($nodeList, workflowSourceParameters);
						// @ts-ignore
						this.jsp?.makeTarget($nodeList, workflowTargetParameters);

						const statesToRemove = [];
						for (const [k, action] of this.schema.actions.entries()) {
							statesToRemove.length = 0;
							for (const stateKid of Object.keys(action.statefrom)) {
								// @ts-ignore
								const state = this.schema.states[this.schema.getKeyStateFromKid(stateKid)];
								if (state) {
									const connection = this.jsp?.connect({
										source:    `kn-state-${this.id}-${state.id}`,
										target:    `kn-action-${this.id}-${action.id}`,
										connector: action.style.type,
										cssClass:  action.style.getCSS(),
										// @ts-ignore
										anchors: ['Continuous'],
									});
									if (connection) {
										// @ts-ignore
										connection.getOverlay('label').setLabel('FROM');
										this.schema.actions[k].statefrom[stateKid] = connection;
									}
								}
								else {
									console.warn(`Unable to find state ${stateKid} in action`, action.getName());
									statesToRemove.push(stateKid);
								}
							}
							statesToRemove.map(state => delete action.statefrom[state]);

							statesToRemove.length = 0;
							for (const stateKid of Object.keys(action.stateto)) {
								// @ts-ignore
								const state = this.schema.states[this.schema.getKeyStateFromKid(stateKid)];
								if (state) {
									const connection = this.jsp?.connect({
										source:    `kn-action-${this.id}-${action.id}`,
										target:    `kn-state-${this.id}-${state.id}`,
										connector: action.style.type,
										cssClass:  action.style.getCSS(),
										// @ts-ignore
										anchors: ['Continuous'],
									});
									if (connection) {
										// @ts-ignore
										connection.getOverlay('label').setLabel('TO');
										this.schema.actions[k].stateto[stateKid] = connection;
									}
								}
								else {
									console.warn(`Unable to find state ${stateKid} in action`, action.getName());
									statesToRemove.push(stateKid);
								}
							}
							statesToRemove.map(state => delete action.stateto[state]);
						}
					});
				}

				// @ts-ignore
				this.jsp?.fire('jsPlumbWorkflowLoaded', this.jsp);
				this.loaded = true;
			}
		});
	}

	/**
	 * Generates the schema
	 *
	 * @returns {Promise<boolean>} Promise result
	 */
	async generate() {
		const result = await APIV5.instance.wfWorkflowSetToK(
			{ objid: `K_NODE;${this.id}` },
			{ id: 'admin-wf-designer.generate' },
		);
		this.Notification.info({
			title:   'Opération terminée avec succès',
			message: 'Ce workflow a bien été injecté en mémoire',
		});
		return !!result;
	}

	/**
	 * Imports a JSON workflow schema
	 *
	 * @param {File} file File to import
	 */
	import(file) {
		const fr = new FileReader();

		fr.addEventListener('load', () => {
			let newSchema;

			try {
				newSchema = JSON.parse(fr.result?.toString() || '');
			}
			catch {
				this.Notification.error({
					title:   this.$translate.instant('error.title'),
					message: this.$translate.instant('error.no-json-file'),
				});
			}

			this.schema.load(newSchema);

			if (this.jsp) {
				this.$timeout(() => {
					this.loaded = false;
					this.display();
				});
			}
		});
		if (file) {
			fr.readAsText(file);
		}
	}

	/**
	 * Initializes the jsPlumb instance
	 *
	 * @param {import('jsplumb').jsPlumbInstance & import('~/scripts/services/model/designer.js').jsPlumbExtra} jsp
	 */
	init(jsp) {
		this.$timeout(() => {
			this.jsp = jsp;

			this.jspOptions.ConnectionOverlays.push(
				[
					'Label',
					{
						// @ts-ignore
						cssClass: 'link',
						id:       'label',
						label:    (/** @type {any} */ label) => {
							if ($(label.component.source).hasClass('state')) {
								return 'FROM';
							}
							if ($(label.component.source).hasClass('action')) {
								return 'TO';
							}
							return '';
						},
					},
				],
			);

			// @ts-ignore
			this.jsp.revalidateEverything = () => {
				// @ts-ignore
				if (this.jsp?.Defaults.Container) {
					// @ts-ignore
					const visibleElements = this.jsp.Defaults.Container.find('._jsPlumb_ng_endpoint:visible');
					// eslint-disable-next-line unicorn/no-useless-spread
					for (const element of [...visibleElements]) {
						// @ts-ignore
						this.jsp?.revalidate($(element).attr('id'));
					}
				}
			};

			const temporaryStyle = new LinkStyle();
			// @ts-ignore
			this.jsp.connectorClass = temporaryStyle.getCSS();
			this.display();
		});
	}

	/**
	 * Loads an existing workflow
	 *
	 * @param {string} workflowKid Workflow KID
	 * @param {boolean} edit If false then read-only mode
	 * @param {boolean} duplicate Duplicate this schema with new KIDs
	 * @returns {Promise<this | undefined>} Promise result
	 */
	async load(workflowKid, edit, duplicate = false) {
		const node = await this.NodeService.load(workflowKid);
		if (node?.kid && node.isa.objid === 'K_NODE;WF_DESIGNER_SCHEMA') {
			[, this.id] = node.kid.split(';');
			this.loading = true;
			this.schema.loading = true;
			this.schema = new Workflow(this.$injector, duplicate ? null : node.kjson.kid);

			this.schema.kidlist = node.kjson.kidlist || [];

			if (duplicate) {
				this.id = this.User.getRefNr();
			}

			this.schema.setRepre(node.repre, duplicate);

			if (node.kjson.statelist) {
				for (const state of node.kjson.statelist) {
					this.schema.addState(state, duplicate);
				}
			}

			if (node.kjson.actionlist) {
				for (const action of node.kjson.actionlist) {
					this.schema.addAction(action, duplicate);
				}
			}

			if (node.kjson.droit) {
				for (const droit of node.kjson.droit) {
					this.schema.addDroit(droit);
				}
			}

			if (node.kjson.name) {
				this.schema.addRepre(new Repre({
					kid:   `K_NODE;${this.User.getRefNr()}`,
					value: node.kjson.name,
				}));
			}

			if (node.kjson.rolelist) {
				this.schema.kidlist.push(...node.kjson.rolelist);
			}

			if (node.kjson.profillist) {
				this.schema.kidlist.push(...node.kjson.profillist);
			}

			this.edit = edit;
			this.loading = false;
			this.modified = false;
			this.schema.loading = false;
		}
		else if (node?.kid && node.isa.objid === 'K_NODE;WF_WORKFLOW') {
			this.id = node.kid.split(';')[1];
			this.loading = true;

			/** @type {any} */
			const wf = await APIV5.instance.wfWorkflowLoad({ wf_kid: node.kid }, { id: 'admin-wf-designer.load' });

			// we have to rename "droit" in "droitlist" because "droit" is a property of Node class
			if (wf.droit) {
				wf.droitlist = wf.droit;
				delete wf.droit;
			}
			const schema = new Workflow(this.$injector, node.kid);
			schema.kidlist = wf.nodes || wf.dico_nodes || [];

			this.schema = Object.assign(schema, wf);
			this.schema.loading = true;

			this.schema.repre = [];
			if (wf.repre) {
				for (const r of wf.repre) {
					this.schema.addRepre(new Repre({
						kid:   r.kid,
						lang:  new Lang({ kid: r.lang_kid }),
						type:  r.type_kid,
						value: r.name ?? r.value,
					}));
				}
			}

			if ('rolelist' in wf) {
				this.schema.kidlist.push(...wf.rolelist);
			}

			if ('profillist' in wf) {
				this.schema.kidlist.push(...wf.profillist);
			}

			this.edit = edit;
			this.loading = false;
			this.modified = false;
			this.schema.loading = false;
		}
		else {
			this.Notification.warning({
				title:   this.$translate.instant('error.title'),
				message: this.$translate.instant('designer.object-not-supported'),
			});
			return undefined;
		}

		return this;
	}

	/**
	 * @param {Action | State} node
	 * @param {any} op
	 */
	nodeAdd(node, op) {
		const $wfEditor = $('.tab-pane.active').find('.schema-editor');

		/** @type {string} */
		let prefix;
		// eslint-disable-next-line no-param-reassign
		if (node instanceof State) {
			this.schema.states.push(node);
			prefix = 'kn-state';
		}
		else if (node instanceof Action) {
			this.schema.actions.push(node);
			prefix = 'kn-action';
		}
		this.$timeout(() => {
			node.selected = false;
			const $element = $(`#${prefix}-${this.id}-${node.id}`);
			this.jsp?.draggable($element, {
				// @ts-ignore
				containment:   $wfEditor,
				stop:          (/** @type {any} */ element) => this.nodeMoveStop(element),
				disabled:      !this.edit,
				snapThreshold: 13,
				grid:          [
					GRID,
					GRID,
				],
			});
			// @ts-ignore
			this.jsp?.makeSource($element, workflowSourceParameters);
			// @ts-ignore
			this.jsp?.makeTarget($element, workflowTargetParameters);
			if (op && node instanceof Action) {
				this.actionUpdateConnections(node, op);
			}
			this.modified = true;

			if (this.selected) {
				this.selected.node.selected = false;
				this.selected = null;
			}
		});
	}

	/**
	 * @returns {Promise<boolean>}
	 */
	async nodeConfirmChanges() {
		let done = true;
		if (this.selected) {
			const modal = new Confirm(this.$injector);
			const label = `designer.confirm-save-${this.selected.node instanceof State ? 'state' : 'action'}`;
			done = await modal.open({
				title:  this.$translate.instant('common.confirmation'),
				body:   this.$sce.trustAsHtml(this.$injector.get('$filter')('sprintf')(
					this.$translate.instant(label),
					this.selected.node.getName(),
				)),
				cancel: {
					label: this.$translate.instant('common.cancel'),
				},
				accept: {
					label: this.$translate.instant('common.save'),
				},
			});
			if (done) {
				this.$rootScope.$broadcast('admin-wf-submit-node');
			}
		}
		return done;
	}

	/**
	 * Adds an action or state from the context menu
	 *
	 * @param {MouseEvent} event Mouse event
	 * @param {Action|State} node Node to create
	 * @returns {Promise<Action | State>} Promise result
	 */
	async nodeCreateFromEvent(event, node) {
		const id = this.User.getRef();

		node.kid = `K_NODE;${id}`;
		node.id = id;
		node.x = Math.round((event.offsetX / this.scale) / GRID) * GRID;
		node.y = Math.round((event.offsetY / this.scale) / GRID) * GRID;
		node.addRepre(new Repre());

		return node;
	}

	/**
	 * Deletes a node (state or action) with confirmation
	 *
	 * @param {Action | State} node Action or State instance
	 * @returns {Promise<boolean>} Promise result
	 */
	async nodeDelete(node) {
		const modal = new ConfirmDeleteModal(this.$injector);
		const answer = await modal.open(node.getName() || '');

		if (answer) {
			if (node instanceof State) {
				this.stateDelete(node);
			}
			else if (node instanceof Action) {
				this.actionDelete(node);
			}
			this.modified = true;
		}

		if (this.selected) {
			this.selected.node.selected = false;
			this.selected = null;
		}

		return answer;
	}

	/**
	 * Displays the node (state or action) for creation or edition
	 *
	 * @param {Action | State} node Instance of the state or action to edit
	 * @param {'info' | 'droit' | 'allocator' | 'delegation'} tab Default tab to display
	 * @param {boolean} create Add node for creation
	 * @param {any} operations Initial operations to launch after node edition
	 */
	async nodeEdit(node, tab, create, operations = {}) {
		// Deselect previously selected node
		let done = true;
		if (this.selected) {
			if (this.selected.modified) {
				done = await this.nodeConfirmChanges();
				if (!done) {
					this.selected.node.selected = false;
					// this.selected.node = node;
					done = true;
				}
			}
			else {
				this.selected.node.selected = false;
			}
		}

		if (done) {
			node.selected = true;

			this.selected = {
				modified: false,
				operations,
				create,
				node,
				tab,
			};
		}
	}

	/**
	 * Callback function after dragging an node (state or action) element on the drop area
	 *
	 * @param {any} element Handler element
	 */
	nodeMoveStop(element) {
		// @ts-ignore
		const node = $(element.e.target).scope().$parent.state || $(element.e.target).scope().$parent.action;
		if (element.pos[0] / this.scale !== node.x || element.pos[1] / this.scale !== node.y) {
			_.merge(node, {
				x: element.pos[0] / this.scale,
				y: element.pos[1] / this.scale,
			});
			this.$rootScope.$apply(() => {
				this.modified = true;
			});
		}
	}

	/**
	 * @param {Action | State} node
	 * @param {any} op
	 */
	nodeUpdate(node, op) {
		if (!this.selected?.node) {
			return;
		}

		if (this.selected.node instanceof State) {
			// this.selected.node.allocators.length = 0;
			// if (Array.isArray(this.selected.node.delegationGranted)) {
			// 	this.selected.node.delegationGranted.length = 0;
			// }
			// this.selected.node.droit.length = 0;
			// this.selected.node.repre.length = 0;
			_.extend(this.selected.node, node);
		}
		else if (this.selected.node instanceof Action) {
			// if (node instanceof Action) {
			// 	node.statefrom = {};
			// 	node.stateto = {};
			// }
			this.selected.node.droit.length = 0;
			_.extend(this.selected.node, node);
			for (const [label, flow] of Object.entries({
				FROM: 'statefrom',
				TO:   'stateto',
			})) {
				// @ts-ignore
				for (const connection of Object.values(this.selected.node[flow])) {
					connection.canvas.classList = [];
					connection.getOverlay('label').setLabel(label);
					connection.setConnector(this.selected.node.style.type);
					connection.removeOverlay('arrow');
					connection.addOverlay([
						'PlainArrow',
						{
							id:       'arrow',
							length:   9,
							location: 1,
						},
					]);
					connection.removeOverlay('label');
					connection.addOverlay([
						'Label',
						{
							cssClass: 'link',
							id:       'label',
							label,
						},
					]);
					connection.addClass(this.selected.node.style.getCSS());
				}
			}
			this.actionUpdateConnections(this.selected.node, op);
		}
		this.selected.node.selected = false;
		this.selected = null;
		this.modified = true;
	}

	/**
	 * Saves the schema
	 *
	 * @returns {Promise<any>} Promise result
	 */
	async save() {
		await super.save();
		this.$rootScope.$broadcast('admin-wf-list-schemas');
	}

	/**
	 * Deletes a state
	 *
	 * @param {State} state State instance
	 */
	stateDelete(state) {
		// check if states exists
		if (state.kid && this.schema.states.some((/** @type {State} */ s) => state.kid === s.kid)) {
			// Suppression des liens dépendants de cet état dans les actions
			for (const action of this.schema.actions) {
				delete action.statefrom[state.kid];
				delete action.stateto[state.kid];
			}

			state.visible = false;
			this.jsp?.remove(`kn-state-${this.id}-${state.id}`);
			this.modified = true;
		}
	}

	/**
	 * Deletes a state
	 *
	 * @param {State} state State instance
	 */
	async subscriptionSet(state) {
		// noinspection JSCheckFunctionSignatures
		this.$q
			.when(APIV5.instance.wfSubscriptionSet(
				// @ts-ignore
				{ objnum: state.kid },
				{ id: 'admin-wf-designer.subscription-set' },
			))
			.then(() => this.Notification.info({
				title:   this.$translate.instant('common.info'),
				message: this.$translate.instant('workflow.successful-subscription'),
			}))
			.catch(error => {
				console.error(error);
			});
	}

	/**
	 * Display the workflow rights for editing
	 */
	async workflowEditDroits() {
		const newNode = await this.$uibModal.open({
			ariaLabelledBy:  'modal-droit-title',
			ariaDescribedBy: 'modal-droit-body',
			template:        templateModalDroit,
			controller:      ModalDroitController,
			controllerAs:    '$ctrl',
			resolve:         {
				node: () => this.schema,
				edit: () => this.edit,
			},
		}).result;

		this.$timeout(() => {
			this.schema.droit = newNode.droit;
			this.modified = true;
		});
	}
}
